import * as React from 'react'
import { View, Dimensions, TouchableOpacity, FlatList, Text } from 'react-native'
import styled from '@theme/styled-components'

import { Viewer } from '@components/plan/ViewerComponent'
import Picker from '@components/picker/Picker'
import IncidentCard from '@components/incidentCard/IncidentCard'
import SimpleAlert from '@components/modal/SimpleAlert'

import useReducer from '@store/useReducer'
import * as LocationStore from '@store/location/location'
import * as SiteStore from '@store/site/site'
import * as IncidentStore from '@store/incident/incident'
import * as FiltreStore from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'

import { ViperPin, ViperViewProps } from 'viper-viewer/dist/types'
import { findLocalisationParents, getMapPins } from '@screens/incident/utils'
import { paramsToSearch } from '@api/utils'
import useBreakpoint from '@utils/useBreakpoints'

type Props = {
  incidents: IncidentResponse[]
  parentHeight: number
  filtersLocationIds: string[]
  plans: Plan[]
}

const DEFAULT_INIT: ViperViewProps['initialPosition'] = {
  center: [2.273982551847581, 48.82340600971497],
  zoom: 14,
}

const IncidentMap = ({ incidents, parentHeight, filtersLocationIds, plans }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [, bp] = useBreakpoint()
  const scroll = React.useRef<FlatList>(null)

  const [displayIncident, setDisplayIncident] = React.useState<string>()
  const [headerHeight, setHeaderHeight] = React.useState(0)

  const places = useReducer(LocationStore.store, (s) => s.places)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations!)
  const manifestation = useReducer(SiteStore.store, (s) => s.manifestation)
  const locationId = useReducer(FiltreStore.store, (s) => s.incident.mapLocationId)

  const canCreateIncident = !manifestation || !!manifestations.inProgress.find((m) => m.id === manifestation.id)

  // Scroll au pin sélectionné dans la flatlist
  React.useEffect(() => {
    if (displayIncident) {
      const index = filteredData.findIndex((inc) => inc.id === displayIncident)
      if (scroll.current && index > -1) {
        scroll.current.scrollToIndex({ animated: true, index })
      }
    }
  }, [displayIncident, scroll])

  const usablePlans = React.useMemo(
    () => plans.filter((plan) => !!plan.viewers && !!plan.viewers.geojson && !!plan.viewers.geojson.path),
    [plans]
  )

  const pickerOptions = React.useMemo(() => {
    const spaces = places.filter((p) => !p.parentId && !!usablePlans.find((plan) => plan.localisationId === p.id))

    const options = spaces
      .map((location) => ({ label: location?.libelle, value: location?.id }))
      .sort((a, b) => a.label?.localeCompare(b.label))
      .filter((option) => filtersLocationIds.length === 0 || filtersLocationIds.includes(option.value))

    if (options.length > 0 && (!locationId || !options.map((o) => o.value).includes(locationId))) {
      FiltreStore.actions.setIncidentFilter({ mapLocationId: options[0].value })
    }
    return options
  }, [places, filtersLocationIds, usablePlans, locationId])

  const location = React.useMemo(
    () => (locationId ? pickerOptions.find((op) => op.value === locationId) : undefined) || pickerOptions[0],
    [locationId, pickerOptions]
  )

  const currentPlan = React.useMemo(() => {
    const current = usablePlans.find((plan) => plan.localisationId === location?.value)
    if (!!current && !!current.viewers.geojson) {
      return current.viewers.geojson
    }
    return undefined
  }, [location])

  // Incidents filtrés par l'espace sélectionné
  const filteredData = React.useMemo(
    () =>
      incidents
        .filter((i) => {
          if (location) {
            const incidentSpace = places.find((p) => p.id === i.localisationId)
            if (incidentSpace) {
              const topParent = findLocalisationParents(places, incidentSpace)[0]

              if (topParent) {
                return topParent.id === location.value
              }
            }
          }
          return false
        })
        .filter((incident) => !!incident.plan?.x && !!incident.plan?.y),
    [incidents, location]
  )

  const onClickPin = (pinId: string) => {
    setDisplayIncident(pinId)
  }

  const onClickMap = (x: number, y: number) => {
    SimpleAlert.open({
      title: i18n.t('screens.incidentList.map.create'),
      description: i18n.t('screens.incidentList.map.createDescription'),
      buttons: [
        { label: i18n.t('common.cancel'), onClick: () => SimpleAlert.close(), color: 'cancel' },
        {
          label: i18n.t('common.confirm'),
          onClick: () => {
            SimpleAlert.close()
            IncidentStore.actions.resetForm()
            navigation.push(
              `/incident/manif${paramsToSearch({ spaceId: location?.value, x: x.toString(), y: y.toString() })}`
            )
          },
        },
      ],
    })
  }

  const pins: ViperPin[] = React.useMemo(() => getMapPins(filteredData), [filteredData])

  const renderIncidentCard = ({ item }: { item: IncidentResponse }) => (
    <CardContainer
      selected={displayIncident === item.id}
      activeOpacity={0.9}
      onPress={() => navigation.push(`/incidentDetail/${item.id}`, { incidentId: item.id })}
      key={item.id}>
      <IncidentCard incident={item} />
    </CardContainer>
  )

  const renderPicker = () => (
    <Picker
      onChange={(opt) => {
        if (!!opt) {
          FiltreStore.actions.setIncidentFilter({ mapLocationId: opt.value })
        }
      }}
      value={location}
      options={pickerOptions}
      placeholder={i18n.t('screens.map.placeholder')}
    />
  )

  const renderViewer = (path: string, params?: PlanViewerParams) => {
    const initialPosition: ViperViewProps['initialPosition'] | undefined = params
      ? { center: params.center ? [params.center.x, params.center.y] : undefined, zoom: params.zoom || undefined }
      : undefined

    return (
      <Viewer
        key={`${path}_${location?.value}`}
        planUrl={path}
        pins={pins}
        // TODO : recevoir ces positions par défaut, sinon le point de départ est arbitraire
        initialPosition={initialPosition || DEFAULT_INIT}
        onClickMap={canCreateIncident ? onClickMap : undefined}
        onClickPin={onClickPin}
      />
    )
  }
  const renderNoPlan = () => (
    <NoPlanContainer>
      <NoPlanText>{i18n.t('screens.map.noPlan')}</NoPlanText>
    </NoPlanContainer>
  )

  const renderMapWeb = () => (
    <MainContainer height={parentHeight}>
      <MapContainer>
        <PickerContainer>{renderPicker()}</PickerContainer>
        {!!currentPlan?.path ? renderViewer(currentPlan.path, currentPlan.params) : renderNoPlan()}
      </MapContainer>
      <View>
        <FlatList
          ref={scroll}
          data={filteredData}
          renderItem={renderIncidentCard}
          keyExtractor={(item) => item.id}
          contentContainerStyle={{ padding: 16, alignItems: 'center', minWidth: 390 }}
          ListEmptyComponent={<MissingInfo>{i18n.t('screens.incidentList.empty')}</MissingInfo>}
          numColumns={1}
          initialScrollIndex={0}
          // le scrollTo ne fonctionnera plus au delà de 200 items dans la liste
          initialNumToRender={200}
          // Permet de prévenir un crash en cas d'erreur de scroll
          onScrollToIndexFailed={() => null}
        />
      </View>
    </MainContainer>
  )

  const renderMapMobile = () => (
    <>
      <MobilePicker onLayout={(e) => setHeaderHeight(e.nativeEvent.layout.height)}>{renderPicker()}</MobilePicker>

      {parentHeight > 0 && headerHeight > 0 && (
        <View style={{ height: parentHeight - headerHeight }}>
          {!!currentPlan?.path ? renderViewer(currentPlan.path, currentPlan.params) : renderNoPlan()}
        </View>
      )}
      {!!displayIncident && (
        <Modal onPress={() => setDisplayIncident(undefined)}>
          <TouchableOpacity
            activeOpacity={0.9}
            onPress={() => navigation.push(`/incidentDetail/${displayIncident}`, { incidentId: displayIncident })}>
            <IncidentCard incident={incidents.find((i) => i.id === displayIncident)!} />
          </TouchableOpacity>
        </Modal>
      )}
    </>
  )

  return bp !== 'phone' ? renderMapWeb() : renderMapMobile()
}

export default IncidentMap

const MainContainer = styled(View)<{ height: number }>`
  flex-direction: row;
  height: ${(props) => props.height}px;
`
const MapContainer = styled(View)`
  ${(props) =>
    props.theme.breakpoint === 'phone' ? `height: ${Dimensions.get('window').height}, zIndex: -1` : `flex:1`};
`

const CardContainer = styled(TouchableOpacity)<{ selected?: boolean }>`
  margin: 8px 0;
  border-radius: 8px;
  border: 3px solid ${(props) => ` ${props.selected ? props.theme.colors.primary : 'transparent'}`};
`
const PickerContainer = styled(View)`
  position: absolute;
  top: 24px;
  left: 24px;
  min-width: 260px;
  z-index: 1;
`
const Modal = styled(TouchableOpacity)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: transparent;
  justify-content: flex-end;
  padding: 16px;
`
const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  padding: 16px 0;
`
const MobilePicker = styled(View)`
  z-index: 1;
  background-color: ${(props) => props.theme.colors.primary};
  padding: 16px;
`
const NoPlanContainer = styled(View)`
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`
const NoPlanText = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`
